import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { admin } from '../utils/api';

const SECTOR_LABELS = {
    property: 'Property',
    tech_product: 'Tech & Product',
    tech: 'Tech & Product',
    sales_account: 'Sales & Account Management',
    sales: 'Sales & Account Management',
    creative_events: 'Creative & Events',
    creative: 'Creative & Events',
    hr_people: 'HR & People',
    hr: 'HR & People',
    marketing_growth: 'Marketing & Growth',
    marketing: 'Marketing & Growth',
    operations_admin: 'Operations & Admin',
    operations: 'Operations & Admin',
    finance_accounting: 'Finance & Accounting',
    finance: 'Finance & Accounting',
    legal_compliance: 'Legal & Compliance',
    legal: 'Legal & Compliance',
};

const STATUS_STYLES = {
    completed: { label: 'Completed', className: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
    in_progress: { label: 'In Progress', className: 'bg-amber-100 text-amber-700 border-amber-300' },
    not_started: { label: 'Not Started', className: 'bg-slate-100 text-slate-500 border-slate-300' },
};

const fmtDateTime = (v) => (v ? new Date(v).toLocaleString('en-GB') : '—');

const Field = ({ label, value, testId }) => {
    if (value === undefined || value === null || value === '') return null;
    return (
        <div data-testid={testId}>
            <div className="text-xs uppercase tracking-wide text-slate-400 font-mono">{label}</div>
            <div className="text-sm font-medium text-slate-900 mt-0.5">{value}</div>
        </div>
    );
};

/**
 * AdminCandidateDetailModal — read-only click-through detail view for a
 * single candidate, opened from the admin Candidates drill-down table.
 * Pulls from GET /admin/candidates/{id} (new, additive, read-only).
 */
const AdminCandidateDetailModal = ({ candidateId, onClose }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError(null);
        admin.getCandidateDetail(candidateId)
            .then((res) => { if (active) setData(res.data); })
            .catch(() => { if (active) setError('Failed to load candidate detail.'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [candidateId]);

    const candidate = data?.candidate;
    const talentCard = candidate?.talent_card;
    const header = talentCard?.header || {};
    const snapshot = talentCard?.snapshot_score || {};
    const status = STATUS_STYLES[data?.assessment_status] || STATUS_STYLES.not_started;

    return (
        <div
            className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4 backdrop-blur-sm"
            onClick={onClose}
            data-testid="admin-candidate-detail-overlay"
        >
            <div
                className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] shadow-2xl p-6 flex flex-col"
                onClick={(e) => e.stopPropagation()}
                data-testid="admin-candidate-detail-modal"
            >
                <div className="flex items-center justify-between mb-4 shrink-0">
                    <h3 className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }} data-testid="admin-candidate-detail-title">
                        {candidate?.name || 'Candidate Detail'}
                    </h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="admin-candidate-detail-close">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="overflow-y-auto flex-1">
                    {loading ? (
                        <div className="py-10 text-center text-slate-400 text-sm" data-testid="admin-candidate-detail-loading">Loading…</div>
                    ) : error ? (
                        <div className="py-10 text-center text-red-500 text-sm" data-testid="admin-candidate-detail-error">{error}</div>
                    ) : (
                        <div className="space-y-6">
                            <div className="flex items-center gap-3">
                                <span
                                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${status.className}`}
                                    data-testid="admin-candidate-detail-status"
                                >
                                    {status.label}
                                </span>
                                {data?.microtest_progress && (
                                    <span className="text-xs text-slate-500" data-testid="admin-candidate-detail-microtest-progress">
                                        Reached microtest step ({SECTOR_LABELS[data.microtest_progress.sector] || data.microtest_progress.sector || '—'})
                                    </span>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <Field label="Email" value={candidate?.email} testId="admin-candidate-detail-email" />
                                <Field label="Signup Date/Time" value={fmtDateTime(candidate?.created_at)} testId="admin-candidate-detail-signup" />
                            </div>

                            {data?.assessment_status === 'completed' && (
                                <div className="rounded-xl border border-purple-200 bg-purple-50/50 p-4 space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <Field
                                            label="Snapshot Score"
                                            value={snapshot.calculated_snapshot_score != null ? `${snapshot.calculated_snapshot_score} /5` : null}
                                            testId="admin-candidate-detail-score"
                                        />
                                        <Field
                                            label="Sector"
                                            value={SECTOR_LABELS[header.sector || candidate?.talent_card?.header?.sector] || header.sector}
                                            testId="admin-candidate-detail-sector"
                                        />
                                        <Field label="Primary Role" value={candidate?.primary_role || header.primary_role} testId="admin-candidate-detail-primary-role" />
                                        <Field label="Secondary Role" value={candidate?.secondary_role || header.secondary_role} testId="admin-candidate-detail-secondary-role" />
                                        <Field label="Title Tag" value={talentCard?.title_tag?.title} testId="admin-candidate-detail-title-tag" />
                                        <Field label="Phase Badge" value={candidate?.phase_badge} testId="admin-candidate-detail-phase-badge" />
                                        <Field label="Location" value={header.location} testId="admin-candidate-detail-location" />
                                        <Field label="Work Preference" value={header.work_preference} testId="admin-candidate-detail-work-preference" />
                                        <Field label="Availability Start" value={header.availability_start} testId="admin-candidate-detail-availability-start" />
                                        <Field
                                            label="Founding Member #"
                                            value={data?.founding_member_number != null ? `#${data.founding_member_number}` : null}
                                            testId="admin-candidate-detail-founding-number"
                                        />
                                    </div>
                                    {Array.isArray(snapshot.key_strengths) && snapshot.key_strengths.length > 0 && (
                                        <div data-testid="admin-candidate-detail-key-strengths">
                                            <div className="text-xs uppercase tracking-wide text-slate-400 font-mono mb-1">Key Strengths</div>
                                            <ul className="text-sm text-slate-800 list-disc list-inside space-y-0.5">
                                                {snapshot.key_strengths.map((s, i) => <li key={i}>{s}</li>)}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            )}

                            {(candidate?.location || candidate?.experience || candidate?.availability ||
                                (candidate?.skills && candidate.skills.length > 0) ||
                                (candidate?.preferred_sectors && candidate.preferred_sectors.length > 0)) && (
                                <div className="grid grid-cols-2 gap-4">
                                    <Field label="Profile Location" value={candidate?.location} testId="admin-candidate-detail-profile-location" />
                                    <Field label="Experience" value={candidate?.experience} testId="admin-candidate-detail-experience" />
                                    <Field label="Availability" value={candidate?.availability} testId="admin-candidate-detail-availability" />
                                    <Field label="Skills" value={candidate?.skills?.join(', ')} testId="admin-candidate-detail-skills" />
                                    <Field label="Preferred Sectors" value={candidate?.preferred_sectors?.map(s => SECTOR_LABELS[s] || s).join(', ')} testId="admin-candidate-detail-preferred-sectors" />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminCandidateDetailModal;
